import React, { useState, useEffect, useCallback } from "react";
import {
  Typography,
  useTheme,
  Box,
  Button,
  Switch,
  CircularProgress,
  IconButton,
} from "@mui/material";
import { CustomTextField, SectionTitle } from "~components/atoms";
import { useStreamContext } from "~contexts/Stream";
import { useToastNotificationContext } from "~contexts/ToastNotification";
import { authFetch } from "~helpers/authFetch";

interface Presenter {
  id: string;
  name: string;
  photoUrl: string;
  veste: string; // tamanho
  calcado: string; // tamanho
  busto: string; // cm
  cintura: string; // cm
  quadril: string; // cm
  altura: string; // m
}

const Medidas: React.FC = () => {
  const theme: any = useTheme();
  const { stream, currentStream } = useStreamContext();
  const { setToastData } = useToastNotificationContext();
  const [measureEnabled, setMeasureEnabled] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [presenters, setPresenters] = useState<Presenter[]>([]);

  // Carregar estado inicial do switch
  useEffect(() => {
    if (stream) {
      const hasMeasure = stream.has_measure === 1;
      setMeasureEnabled(hasMeasure);
    } else {
      setMeasureEnabled(false);
    }
  }, [stream]);

  // Função para carregar apresentadores do backend
  const loadPresenters = useCallback(async () => {
    if (!currentStream?.id) {
      return;
    }

    setLoading(true);
    try {
      const response = await authFetch({
        path: `${process.env.REACT_APP_BACKEND_URL}/measure/${currentStream.id}/presenters`,
        method: "GET",
      });

      if (response.ok) {
        const data = await response.json();
        if (
          data.presenters &&
          Array.isArray(data.presenters) &&
          data.presenters.length > 0
        ) {
          // Garantir que cada apresentador tem um ID único
          const presentersWithIds = data.presenters.map((p: any, index: number) => ({
            ...p,
            id:
              p.id || `${Date.now()}-${index}-${Math.random().toString(36).substr(2, 9)}`,
          }));
          setPresenters(presentersWithIds);
        } else {
          // Se não há dados, manter lista vazia (nenhum componente aberto)
          setPresenters([]);
        }
      } else {
        // Se não há dados no backend, manter lista vazia
        setPresenters([]);
      }
    } catch (error) {
      console.error("Error loading presenters:", error);
      setPresenters([]);
    } finally {
      setLoading(false);
    }
  }, [currentStream?.id]);

  // Carregar dados dos apresentadores quando a stream mudar
  useEffect(() => {
    if (currentStream?.id) {
      loadPresenters();
    }
  }, [currentStream?.id, loadPresenters]);

  // Função para salvar apenas has_measure
  const handleToggleMeasure = useCallback(
    async (newValue: boolean) => {
      if (!currentStream?.id) {
        setToastData(
          {
            title: "Erro",
            text: "Nenhuma stream selecionada",
          },
          "error"
        );
        return;
      }

      try {
        setSaving(true);

        const response = await authFetch({
          path: `${process.env.REACT_APP_BACKEND_URL}/measure/${currentStream.id}/has-measure`,
          method: "PATCH",
          data: { has_measure: newValue ? 1 : 0 },
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          const error = new Error(errorData.message || "Erro ao salvar medidas");
          (error as any).status = response.status;
          throw error;
        }

        setMeasureEnabled(newValue);

        setToastData(
          {
            title: "Sucesso",
            text: "Componente atualizado com sucesso",
          },
          "success"
        );
      } catch (error: any) {
        console.error("Error saving has_measure:", error);
        setMeasureEnabled(!newValue);

        let errorMessage = "Erro ao salvar medidas";

        if (error.status === 400) {
          errorMessage = error.message || "Dados inválidos";
        }

        setToastData(
          {
            title: "Erro",
            text: errorMessage,
          },
          "error"
        );
      } finally {
        setSaving(false);
      }
    },
    [currentStream, setToastData]
  );

  // Função para salvar apresentadores
  const handleSavePresenters = useCallback(async () => {
    if (!currentStream?.id) {
      setToastData(
        {
          title: "Erro",
          text: "Nenhuma stream selecionada",
        },
        "error"
      );
      return;
    }

    try {
      setSaving(true);

      const response = await authFetch({
        path: `${process.env.REACT_APP_BACKEND_URL}/measure/${currentStream.id}/presenters`,
        method: "PATCH",
        data: { presenters },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const error = new Error(errorData.message || "Erro ao salvar apresentadores");
        (error as any).status = response.status;
        throw error;
      }

      setToastData(
        {
          title: "Sucesso",
          text: "Apresentadores salvos com sucesso",
        },
        "success"
      );
    } catch (error: any) {
      console.error("Error saving presenters:", error);

      let errorMessage = "Erro ao salvar apresentadores";

      if (error.status === 400) {
        errorMessage = error.message || "Dados inválidos";
      }

      setToastData(
        {
          title: "Erro",
          text: errorMessage,
        },
        "error"
      );
    } finally {
      setSaving(false);
    }
  }, [currentStream?.id, presenters, setToastData]);

  // Função para adicionar novo apresentador
  const handleAddPresenter = () => {
    const newId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    setPresenters([
      ...presenters,
      {
        id: newId,
        name: "",
        photoUrl: "",
        veste: "",
        calcado: "",
        busto: "",
        cintura: "",
        quadril: "",
        altura: "",
      },
    ]);
  };

  // Função para remover apresentador
  const handleRemovePresenter = (presenterId: string) => {
    setPresenters(presenters.filter((p) => p.id !== presenterId));
  };

  // Função para atualizar um apresentador
  const updatePresenter = (id: string, field: keyof Presenter, value: string) => {
    setPresenters(
      presenters.map((presenter) =>
        presenter.id === id ? { ...presenter, [field]: value } : presenter
      )
    );
  };

  if (!currentStream) {
    return (
      <Box>
        <Typography
          variant="h4"
          sx={{ color: theme.palette.grey[0], mb: 0.5, fontSize: "30px !important" }}
        >
          medidas
        </Typography>
        <Typography
          variant="body2"
          sx={{ color: "#858585", mb: "40px", fontSize: "15px" }}
        >
          Selecione uma marca para editar as medidas
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          mb: 3,
        }}
      >
        <Box>
          <Typography
            variant="h4"
            sx={{ color: theme.palette.grey[0], mb: 0.5, fontSize: "30px !important" }}
          >
            medidas
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: "#858585", mb: "40px", fontSize: "15px" }}
          >
            itens não preenchidos não aparecem
          </Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Typography
              sx={{
                color: theme.palette.grey[0],
                fontSize: "15px",
                fontWeight: 400,
              }}
            >
              ativar componente
            </Typography>
            <Switch
              checked={measureEnabled}
              onChange={(e) => handleToggleMeasure(e.target.checked)}
              disabled={saving}
              sx={{
                width: "40px",
                height: "24px",
              }}
            />
          </Box>
          <Button
            variant="contained"
            onClick={handleSavePresenters}
            disabled={saving || loading}
            sx={{
              backgroundColor: "#FFFFFF",
              color: "#000000",
              borderRadius: "100px",
              paddingTop: "16px",
              paddingRight: "24px",
              paddingBottom: "16px",
              paddingLeft: "24px",
              height: "48px",
              gap: "12px",
              fontWeight: 400,
              fontSize: "11px",
              textTransform: "none",
              "&:hover": {
                backgroundColor: "#F5F5F5",
              },
              "&:disabled": {
                backgroundColor: "#666666",
                color: "#FFFFFF",
              },
            }}
          >
            {saving ? (
              <>
                <CircularProgress size={16} sx={{ color: "#000000" }} />
                salvando...
              </>
            ) : (
              <>
                publicar
                <Box
                  component="img"
                  src="/icons/publish.svg"
                  alt="publicar"
                  sx={{
                    width: "24px",
                    height: "24px",
                  }}
                />
              </>
            )}
          </Button>
        </Box>
      </Box>

      <Box sx={{ maxWidth: "720px" }}>
        {loading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: "400px",
            }}
          >
            <CircularProgress />
          </Box>
        ) : (
          <>
            {presenters.map((presenter, index) => (
              <Box key={presenter.id} sx={{ mb: 4 }}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 2,
                  }}
                >
                  <SectionTitle>apresentador {index + 1}</SectionTitle>
                  <IconButton
                    onClick={() => handleRemovePresenter(presenter.id)}
                    sx={{
                      color: theme.palette.grey[400],
                      padding: "4px",
                      width: "fit-content",
                      "&:hover": {
                        color: theme.palette.error.main,
                        backgroundColor: "transparent",
                      },
                    }}
                  >
                    <Box
                      component="img"
                      src="/icons/delete.png"
                      alt="excluir"
                      sx={{
                        width: "24px",
                        height: "24px",
                      }}
                    />
                  </IconButton>
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    gap: 3,
                    alignItems: "flex-start",
                  }}
                >
                  {/* Área da foto */}
                  <Box
                    sx={{
                      width: "130px",
                      height: "190px",
                      borderRadius: "8px",
                      backgroundColor: theme.palette.grey[700],
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                      overflow: "hidden",
                    }}
                  >
                    <Box
                      component="img"
                      src={
                        presenter.photoUrl || "/icons/frontend-default/medidas-foto.png"
                      }
                      alt={`Apresentador ${index + 1}`}
                      onError={(e) => {
                        // Se a imagem customizada falhar, usar o placeholder padrão
                        if (presenter.photoUrl) {
                          (e.target as HTMLImageElement).src =
                            "/icons/frontend-default/medidas-foto.png";
                        }
                      }}
                      sx={{
                        width: presenter.photoUrl ? "110px" : "32px",
                        height: presenter.photoUrl ? "110px" : "32px",
                        borderRadius: presenter.photoUrl ? "100px" : "0",
                        objectFit: "cover",
                      }}
                    />
                  </Box>

                  {/* Campos de input */}
                  <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 2 }}>
                    {/* Nome do apresentador */}
                    <CustomTextField
                      value={presenter.name}
                      onChange={(e) =>
                        updatePresenter(presenter.id, "name", e.target.value)
                      }
                      label="nome do apresentador"
                      fullWidth
                    />

                    {/* Link da foto */}
                    <CustomTextField
                      value={presenter.photoUrl}
                      onChange={(e) =>
                        updatePresenter(presenter.id, "photoUrl", e.target.value)
                      }
                      label="link da foto"
                      fullWidth
                    />

                    {/* Medidas - 6 campos lado a lado */}
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: "repeat(6, 1fr)",
                        gap: 1.5,
                      }}
                    >
                      {/* Veste (tamanho) */}
                      <Box>
                        <CustomTextField
                          value={presenter.veste}
                          onChange={(e) =>
                            updatePresenter(presenter.id, "veste", e.target.value)
                          }
                          label="tamanho"
                          height="44px"
                          inputPadding="0 12px"
                          labelTransform="translate(12px, -8px) scale(1)"
                          inputProps={{ style: { textAlign: "center" } }}
                        />
                        <Typography
                          sx={{
                            color: theme.palette.grey[400],
                            fontSize: "11px",
                            fontWeight: 400,
                            mt: 0.5,
                            textAlign: "center",
                          }}
                        >
                          veste (tamanho)
                        </Typography>
                      </Box>

                      {/* Calçado */}
                      <Box>
                        <CustomTextField
                          value={presenter.calcado}
                          onChange={(e) =>
                            updatePresenter(presenter.id, "calcado", e.target.value)
                          }
                          label="tamanho"
                          height="44px"
                          inputPadding="0 12px"
                          labelTransform="translate(12px, -8px) scale(1)"
                          inputProps={{ style: { textAlign: "center" } }}
                        />
                        <Typography
                          sx={{
                            color: theme.palette.grey[400],
                            fontSize: "11px",
                            fontWeight: 400,
                            mt: 0.5,
                            textAlign: "center",
                          }}
                        >
                          calçado
                        </Typography>
                      </Box>

                      {/* Busto (cm) */}
                      <Box>
                        <CustomTextField
                          value={presenter.busto}
                          onChange={(e) =>
                            updatePresenter(presenter.id, "busto", e.target.value)
                          }
                          label="cm"
                          height="44px"
                          inputPadding="0 12px"
                          labelTransform="translate(12px, -8px) scale(1)"
                          inputProps={{ style: { textAlign: "center" } }}
                        />
                        <Typography
                          sx={{
                            color: theme.palette.grey[400],
                            fontSize: "11px",
                            fontWeight: 400,
                            mt: 0.5,
                            textAlign: "center",
                          }}
                        >
                          busto (cm)
                        </Typography>
                      </Box>

                      {/* Cintura (cm) */}
                      <Box>
                        <CustomTextField
                          value={presenter.cintura}
                          onChange={(e) =>
                            updatePresenter(presenter.id, "cintura", e.target.value)
                          }
                          label="cm"
                          height="44px"
                          inputPadding="0 12px"
                          labelTransform="translate(12px, -8px) scale(1)"
                          inputProps={{ style: { textAlign: "center" } }}
                        />
                        <Typography
                          sx={{
                            color: theme.palette.grey[400],
                            fontSize: "11px",
                            fontWeight: 400,
                            mt: 0.5,
                            textAlign: "center",
                          }}
                        >
                          cintura (cm)
                        </Typography>
                      </Box>

                      {/* Quadril (cm) */}
                      <Box>
                        <CustomTextField
                          value={presenter.quadril}
                          onChange={(e) =>
                            updatePresenter(presenter.id, "quadril", e.target.value)
                          }
                          label="cm"
                          height="44px"
                          inputPadding="0 12px"
                          labelTransform="translate(12px, -8px) scale(1)"
                          inputProps={{ style: { textAlign: "center" } }}
                        />
                        <Typography
                          sx={{
                            color: theme.palette.grey[400],
                            fontSize: "11px",
                            fontWeight: 400,
                            mt: 0.5,
                            textAlign: "center",
                          }}
                        >
                          quadril (cm)
                        </Typography>
                      </Box>

                      {/* Altura (m) */}
                      <Box>
                        <CustomTextField
                          value={presenter.altura}
                          onChange={(e) =>
                            updatePresenter(presenter.id, "altura", e.target.value)
                          }
                          label="cm"
                          height="44px"
                          inputPadding="0 12px"
                          labelTransform="translate(12px, -8px) scale(1)"
                          inputProps={{ style: { textAlign: "center" } }}
                        />
                        <Typography
                          sx={{
                            color: theme.palette.grey[400],
                            fontSize: "11px",
                            fontWeight: 400,
                            mt: 0.5,
                            textAlign: "center",
                          }}
                        >
                          altura (m)
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </Box>
              </Box>
            ))}

            {/* Botão adicionar apresentador */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                mb: 3,
              }}
            >
              <Button
                variant="text"
                onClick={handleAddPresenter}
                sx={{
                  color: theme.palette.grey[400],
                  fontSize: "15px",
                  fontWeight: 400,
                  textTransform: "none",
                  "&:hover": {
                    backgroundColor: "transparent",
                    color: theme.palette.grey[0],
                  },
                }}
              >
                adicionar apresentador +
              </Button>
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
};

export default Medidas;
